import { useEffect, useState } from 'react';
import { useSessionStorage, useUnmount } from 'usehooks-ts';
import { useHasNavigatedBack } from '../../../core/history';

/**
 * THIS IS NOT A GENERIC UTILITY TO BE USED ANYWHERE!
 *
 * This code is specifically tailored to improve usablity of 2 old Backend reports:
 * - Accounting report
 * - Payment report
 *
 * It captures and restores expanded/collapsed state of table rows and buttons (based on `display` style) in those 2
 * reports. The restoration happens only on navigation back, and for this to work, the reports also need to be cached
 * through {@link CACHE_ENABLED_BACKEND_PAGES}
 *
 * The capturing and restoration logic works on an assumption that the order of elements in which they're captured and
 * restored stays the same.
 */
export const RestoreReportExpandedRows = ({
    reportName,
    rowClassPrefix,
}: {
    reportName: string;
    rowClassPrefix: string;
}) => {
    const [mounted, setMounted] = useState(false);
    const [elementStyles, setElementStyles] = useSessionStorage<string[]>(`report-expanded-rows-${reportName}`, []);
    const hasNavigatedBack = useHasNavigatedBack();
    /**
     * This assumes that all table rows have same class prefix (which they had at the time of writing this code).
     * mews-expand and mews-collapse are classes of the expand/collapse buttons.
     */
    const elementSelector = `tr[class^=${rowClassPrefix}], .mews-expand, .mews-collapse`;

    useEffect(() => {
        if (!mounted) {
            setMounted(true);
            window.setTimeout(() => {
                if (hasNavigatedBack) {
                    document
                        .querySelectorAll(elementSelector)
                        .forEach((e, index) => e.setAttribute('style', elementStyles[index]));
                }
            }, 0);
        }
    }, [mounted, elementStyles, elementSelector, hasNavigatedBack]);

    useUnmount(() => {
        const elements = [...document.querySelectorAll(elementSelector)].map((e) => e.getAttribute('style') ?? '');
        setElementStyles(elements);
    });

    return null;
};
